import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { isAdmin } = useAuth();
  const { dark, setDark } = useTheme();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const set = (k, v) => setS((o) => ({ ...o, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      ["gst_pct", "default_commission_pct", "default_profit_pct", "margin_alert_threshold"].forEach((k) => payload[k] = Number(payload[k]) || 0);
      await api.put("/settings", payload); toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;

  const fields = [
    ["clinic_name", "Clinic Name", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"],
    ["currency", "Currency", "text"], ["gst_pct", "GST %", "number"],
    ["default_commission_pct", "Default Commission %", "number"], ["default_profit_pct", "Default Profit %", "number"],
    ["margin_alert_threshold", "Margin Alert Threshold %", "number"],
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div><h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Settings</h2><p className="text-slate-500 mt-1">Clinic configuration & preferences</p></div>

      <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center justify-between">
          <div><div className="font-medium">Dark Mode</div><div className="text-sm text-slate-500">Toggle the dashboard theme</div></div>
          <Switch checked={dark} onCheckedChange={setDark} data-testid="settings-dark-toggle" />
        </div>
      </Card>

      <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          {fields.map(([k, l, t]) => (
            <div key={k} className="space-y-2"><Label>{l}</Label><Input type={t} value={s[k] ?? ""} disabled={!isAdmin} onChange={(e) => set(k, e.target.value)} data-testid={`set-${k}`} /></div>
          ))}
        </div>
        <div className="space-y-2"><Label>Address</Label><Textarea value={s.address || ""} disabled={!isAdmin} onChange={(e) => set("address", e.target.value)} /></div>
        <div className="space-y-2"><Label>Clinic Logo URL</Label><Input value={s.clinic_logo || ""} disabled={!isAdmin} onChange={(e) => set("clinic_logo", e.target.value)} /></div>
        {isAdmin ? (
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}</Button>
        ) : <p className="text-sm text-slate-400">Read-only. Contact admin to change settings.</p>}
      </Card>
    </div>
  );
}
